/**
 * Auth Redux Slice
 * Manages authentication state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthService } from '../../services/auth.service';
import { getByIdAsync, clearClinic, getStaffByIdAsync } from './clinic.slice';
import { fetchAgencyByIdAsync, clearAgency } from './agency.slice';
import type {
  LoginCredentials,
  User,
  AuthResponse,
  CreateClinicData,
  CreateClinicAdminData,
} from '../../types/auth.types';
import type { ClinicRegistrationData } from '../../types/clinic.types';

/**
 * Auth State Interface
 */
interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

/**
 * Initial State
 */
const initialState: AuthState = {
  user: AuthService.getUser(),
  token: AuthService.getToken(),
  refreshToken: AuthService.getRefreshToken(),
  isAuthenticated: AuthService.isAuthenticated(),
  isLoading: false,
  error: null,
  isInitialized: false,
};

/**
 * Async Thunks
 */

// Fetch Current User (from /api/v1/auth/me)
export const fetchCurrentUserAsync = createAsyncThunk<
  User,
  void,
  { rejectValue: string }
>(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await AuthService.getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch user profile.'
      );
    }
  }
);

// Login
export const loginAsync = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.login(credentials);
      
      // After successful login, fetch complete user data from /api/v1/auth/me
      // Wait a bit to ensure token is set in axios interceptor
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Fetch and persist complete user profile
      let currentUser: User | null = null;
      try {
        currentUser = await dispatch(fetchCurrentUserAsync()).unwrap();
        console.log('✅ User profile fetched and persisted successfully');
      } catch (error) {
        // Log but don't fail login if profile fetch fails
        console.warn('Failed to fetch user profile from /auth/me:', error);
      }
      
      // If user is an agency_admin, fetch the full agency data
      const userRole = (currentUser?.role || response.user?.role || '').toLowerCase();
      if (userRole === 'agency_admin' && currentUser?.agency?.id) {
        try {
          await dispatch(fetchAgencyByIdAsync(currentUser.agency.id)).unwrap();
          console.log('✅ Agency data fetched and persisted successfully');
        } catch (error) {
          // Log but don't fail login if agency fetch fails
          console.warn('Failed to fetch agency data:', error);
        }
      }
      
      // Check if user is a doctor - use staff authentication instead of clinic/me
      const role = response.user?.role?.toUpperCase() || '';
      const isDoctor = role === 'DOCTOR';
      
      if (isDoctor) {
        // For doctors, use staff authentication with staffId instead of clinic/me
        // Check if staffId is in the login response
        const staffId = response.user?.staffId;
        
        if (staffId) {
          try {
            // Fetch staff data using staffId for authorization
            await dispatch(getStaffByIdAsync(staffId)).unwrap();
            console.log('✅ Doctor staff authentication successful with staffId:', staffId);
          } catch (error) {
            // Log but don't fail login if staff fetch fails
            console.warn('Failed to fetch staff data with staffId:', error);
          }
        } else {
          // If staffId is not in response, log warning
          console.warn('⚠️ Doctor login: staffId not found in response');
        }
      }
      
      // If user has a clinicId, also fetch clinic by ID
      if (response.user?.clinicId) {
        try {
          await dispatch(getByIdAsync(response.user.clinicId)).unwrap();
        } catch (error) {
          // Log but don't fail login if clinic/{id} fails
          console.warn('Failed to fetch clinic by ID:', error);
        }
      }
      
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Login failed. Please try again.'
      );
    }
  }
);

// Logout
export const logoutAsync = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'auth/logout',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await AuthService.logout();
      // Clear clinic and agency data on logout
      dispatch(clearClinic());
      dispatch(clearAgency());
      return;
    } catch (error: any) {
      // Clear clinic and agency data even if API call fails
      dispatch(clearClinic());
      dispatch(clearAgency());
      return rejectWithValue(
        error.response?.data?.message || 'Logout failed. Please try again.'
      );
    }
  }
);

// Verify Token
export const verifyTokenAsync = createAsyncThunk<
  User | null,
  void,
  { rejectValue: string }
>(
  'auth/verifyToken',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // First, verify the token is valid
      const verifiedUser = await AuthService.verifyToken();
      
      if (!verifiedUser) {
        return null;
      }
      
      // Fetch complete user profile from /auth/me
      let user: User | null = verifiedUser;
      try {
        user = await AuthService.getCurrentUser();
        console.log('✅ User profile loaded from /auth/me');
      } catch (error) {
        // Fall back to verifyToken user data if /auth/me fails
        console.warn('Failed to fetch from /auth/me, using verifyToken data:', error);
      }
      
      // If user is authenticated, fetch additional data
      if (user) {
        // Check user role
        const role = user?.role?.toUpperCase() || '';
        const isDoctor = role === 'DOCTOR';
        const isAgencyAdmin = user?.role?.toLowerCase() === 'agency_admin';
        
        // If user is an agency_admin, fetch the full agency data
        if (isAgencyAdmin && user.agency?.id) {
          dispatch(fetchAgencyByIdAsync(user.agency.id)).catch(() => {
            // Silently fail if agency fetch fails
            console.warn('Failed to fetch agency data on verify');
          });
        }
        
        if (isDoctor) {
          // For doctors, use staff authentication with staffId instead of clinic/me
          const staffId = user.staffId;
          
          if (staffId) {
            // Fetch staff data using staffId for authorization
            dispatch(getStaffByIdAsync(staffId)).catch(() => {
              // Silently fail if staff fetch fails
            });
          } else {
            // If staffId is not available, log warning
            console.warn('⚠️ Doctor verifyToken: staffId not found');
          }
        }
        
        // If user has a clinicId, also fetch clinic by ID
        if (user.clinicId) {
          dispatch(getByIdAsync(user.clinicId)).catch(() => {
            // Silently fail if endpoint fails
          });
        }
      }
      
      return user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Token verification failed.'
      );
    }
  }
);

// Forgot Password
export const forgotPasswordAsync = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await AuthService.forgotPassword(email);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send password reset email.'
      );
    }
  }
);

// Reset Password
export const resetPasswordAsync = createAsyncThunk<
  { message: string },
  { newPassword: string },
  { rejectValue: string }
>(
  'auth/resetPassword',
  async ({ newPassword }, { rejectWithValue }) => {
    try {
      const response = await AuthService.resetPassword(newPassword);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to reset password.'
      );
    }
  }
);

// Create Clinic
export const createClinicAsync = createAsyncThunk<
  AuthResponse & { clinic?: any; message?: string },
  CreateClinicData | ClinicRegistrationData,
  { rejectValue: string }
>(
  'auth/createClinic',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.createClinic(data);
      
      // Check if clinic is pending approval (no user/token returned)
      if (!response.user || !response.token) {
        // Clinic created but pending admin approval
        // Don't try to fetch clinic data since user isn't authenticated yet
        return response;
      }
      
      // After successful clinic creation with immediate approval, fetch clinic data
      // If user has a clinicId, fetch clinic by ID
      if (response.user?.clinicId) {
        dispatch(getByIdAsync(response.user.clinicId)).catch(() => {
          // Silently fail if endpoint fails
        });
      }
      
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create clinic.'
      );
    }
  }
);

// Create Clinic Admin
export const createClinicAdminAsync = createAsyncThunk<
  AuthResponse,
  CreateClinicAdminData,
  { rejectValue: string }
>(
  'auth/createClinicAdmin',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.createClinicAdmin(data);
      
      // After successful clinic admin creation, fetch clinic data
      // If user has a clinicId, fetch clinic by ID
      if (response.user?.clinicId) {
        dispatch(getByIdAsync(response.user.clinicId)).catch(() => {
          // Silently fail if endpoint fails
        });
      }
      
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create clinic admin.'
      );
    }
  }
);

// Change Password
export const changePasswordAsync = createAsyncThunk<
  { message: string },
  { currentPassword: string; newPassword: string },
  { rejectValue: string }
>(
  'auth/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await AuthService.changePassword(currentPassword, newPassword);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to change password.'
      );
    }
  }
);

/**
 * Auth Slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set user manually
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      AuthService.setUser(action.payload);
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear auth (logout without API call)
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      AuthService.clearAuth();
    },

    // Update user data
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        AuthService.setUser(state.user);
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logoutAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutAsync.rejected, (state) => {
        // Clear state even if API call fails
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });

    // Verify Token
    builder
      .addCase(verifyTokenAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyTokenAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(verifyTokenAsync.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.user = null;
        state.isAuthenticated = false;
        state.token = null;
        state.refreshToken = null;
      });

    // Forgot Password
    builder
      .addCase(forgotPasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPasswordAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(forgotPasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to send password reset email';
      });

    // Reset Password
    builder
      .addCase(resetPasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPasswordAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resetPasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to reset password';
      });

    // Create Clinic
    builder
      .addCase(createClinicAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createClinicAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Check if clinic is pending approval (no user/token)
        if (!action.payload.user || !action.payload.token) {
          // Clinic created but pending admin approval
          // Don't set user/token/isAuthenticated
          state.error = null;
        } else {
          // Clinic approved immediately
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
        }
        
        // Store clinic profile if returned in response
        if ((action.payload as any).clinic) {
          localStorage.setItem('clinicProfile', JSON.stringify((action.payload as any).clinic));
        }
      })
      .addCase(createClinicAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create clinic';
      });

    // Create Clinic Admin
    builder
      .addCase(createClinicAdminAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createClinicAdminAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(createClinicAdminAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create clinic admin';
      });

    // Change Password
    builder
      .addCase(changePasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePasswordAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to change password';
      });

    // Fetch Current User (from /api/v1/auth/me)
    builder
      .addCase(fetchCurrentUserAsync.pending, () => {
        // Don't set isLoading to true to avoid UI flicker
        // This is a background fetch after login
      })
      .addCase(fetchCurrentUserAsync.fulfilled, (state, action) => {
        // Update user with complete profile data from /auth/me
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchCurrentUserAsync.rejected, (_state, action) => {
        // Don't clear auth on profile fetch failure
        // User is still authenticated, just couldn't get full profile
        console.warn('Failed to fetch user profile:', action.payload);
      });
  },
});

// Actions
export const { setUser, clearError, clearAuth, updateUser } = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectIsInitialized = (state: { auth: AuthState }) => state.auth.isInitialized;
export const selectUserAgency = (state: { auth: AuthState }) => state.auth.user?.agency;
export const selectUserNotificationPreferences = (state: { auth: AuthState }) => state.auth.user?.notificationPreferences;

// Reducer
export default authSlice.reducer;

